/**
 * Board-level layer semantics: the pad master, and the board outline.
 *
 * These are the two layer roles that are not expressible as a plain layer
 * *type*, so they get explicit support here rather than being scattered
 * through the renderer, netlist and CAM.
 */

import {
  type CadDocument, type ComponentEntity, type Entity, type Layer,
  LayerType, isElectrical,
} from '../model.ts';
import { padThrough } from './pad.ts';

/**
 * Layer 0 is the pad master.
 *
 * A through-hole pad drawn there belongs to *every* copper layer at once, so a
 * DIP pin connects top to bottom without being duplicated per layer. This is
 * not a convention we could choose differently: the shipped DIP14 has no
 * `Layer:` field on its pads at all, and all 1,935 pads in THD.LIB sit on
 * layer 0. Surface-mount pads are rejected there by the original
 * (OGdlg02.cpp:3495) because a pad on every layer cannot be surface mount.
 */
export const PAD_MASTER_LAYER = 0;

/**
 * True if this pad participates in copper on every layer.
 *
 * A drilled, plated hole connects every layer it passes through, so what makes
 * a pad "pad master" is that it is a *through* type — not which layer it
 * happens to be drawn on. Designs tag a through pad with a plane layer to say
 * which plane it should be thermally relieved into: `Pad:: Layer:"GND" Type:T1`
 * is a hole connected to the ground plane, not a hole that exists only there.
 * Requiring layer 0 as well meant 1256 such pads across 28 of the shipped
 * boards were treated as living on one plane alone, which left them off every
 * copper plot — a via that simply was not drilled in the output.
 */
export function isPadMaster(e: Entity): boolean {
  return e.kind === 'pad' && padThrough(e);
}

/**
 * Which layers a primitive electrically occupies.
 *
 * A through pad occupies all copper and plane layers; everything else occupies
 * only the layer it is drawn on.
 */
export function occupiedLayers(e: Entity, doc: CadDocument): number[] {
  if (!isPadMaster(e)) return [e.layer];
  const out: number[] = [];
  doc.layers.forEach((l, i) => {
    if (isElectrical(l.type)) out.push(i);
  });
  return out.length > 0 ? out : [e.layer];
}

// ---------------------------------------------------------------------------
// Board outline
// ---------------------------------------------------------------------------

export interface Rect {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
}

/**
 * The layer carrying the board outline.
 *
 * The outline decides the physical board: its size, the Edge Cuts plot, the
 * limit for copper pour, and what "the board" means for zoom and placement.
 * It cannot live on layer 0, which is already the pad master, so it uses the
 * first Drawing-type layer — `CAM Control` in the stock stack, which is what
 * that layer is for.
 */
export function outlineLayer(doc: CadDocument): number {
  const named = doc.layers.findIndex((l) => /outline|edge|board/i.test(l.name));
  if (named >= 0) return named;
  const cam = doc.layers.findIndex(
    (l) => l.type === LayerType.Drawing && !/ratnest|ratsnest/i.test(l.name));
  return cam >= 0 ? cam : 0;
}

/**
 * Geometry defining the board edge.
 *
 * The dedicated outline layer comes first, but real CIRCAD boards frequently
 * leave `CAM Control` empty and draw the edge as bare lines on the *pad master*
 * — both 5300quoteB.pcb and PRC47.PCB do exactly that. The pad master otherwise
 * carries pads, so lines and arcs on it are the outline; without this the board
 * edge is invisible to us and everything that asks for it gets the whole drawing
 * sheet instead, title block and all.
 */
export function outlineEntities(doc: CadDocument): Entity[] {
  // A board edge is a board concept. On a schematic there is nothing to find,
  // and its sheet border or bus layer would otherwise masquerade as one.
  if (doc.kind !== 'pcb') return [];

  const onLayer = (layer: number, kinds?: (Entity['kind'])[]): Entity[] =>
    doc.entities.filter((e) => e.layer === layer && e.kind !== 'component'
      && (!kinds || kinds.includes(e.kind)));

  const dedicated = onLayer(outlineLayer(doc));
  if (dedicated.length > 0) return dedicated;

  return onLayer(PAD_MASTER_LAYER, ['line', 'arc']);
}

/**
 * The board's size and scope.
 *
 * Taken from the outline layer when one has been drawn, because that is the
 * physical board. Falls back to the extent of everything, so an unbounded
 * work-in-progress still has usable bounds.
 */
export function boardExtent(doc: CadDocument): { rect: Rect | null; fromOutline: boolean } {
  const outline = outlineEntities(doc);
  if (outline.length > 0) {
    const rect = extentOf(outline);
    if (rect) return { rect, fromOutline: true };
  }
  return { rect: extentOf(doc.entities), fromOutline: false };
}

/** Axis-aligned extent of a set of entities, in document space. */
export function extentOf(entities: Entity[]): Rect | null {
  let x1 = Infinity, y1 = Infinity, x2 = -Infinity, y2 = -Infinity;

  const grow = (ax: number, ay: number, bx = ax, by = ay): void => {
    x1 = Math.min(x1, ax, bx); x2 = Math.max(x2, ax, bx);
    y1 = Math.min(y1, ay, by); y2 = Math.max(y2, ay, by);
  };

  for (const e of entities) {
    switch (e.kind) {
      case 'line': grow(e.x1, e.y1, e.x2, e.y2); break;
      case 'arc': grow(e.cx - e.radius, e.cy - e.radius, e.cx + e.radius, e.cy + e.radius); break;
      case 'pad': grow(e.x - e.sizeX / 2, e.y - e.sizeY / 2, e.x + e.sizeX / 2, e.y + e.sizeY / 2); break;
      case 'text': grow(e.x, e.y, e.x, e.y + e.height); break;
      case 'fill':
        for (const v of e.vertices) grow(v.x, v.y);
        break;
      case 'component': {
        const c = e as ComponentEntity;
        grow(c.x + c.bounds.x1, c.y + c.bounds.y1, c.x + c.bounds.x2, c.y + c.bounds.y2);
        break;
      }
      default: break;
    }
  }

  return Number.isFinite(x1) ? { x1, y1, x2, y2 } : null;
}

/** Board size in inches, or null when no outline or geometry exists. */
export function boardSize(doc: CadDocument): { width: number; height: number } | null {
  const { rect } = boardExtent(doc);
  if (!rect) return null;
  return { width: rect.x2 - rect.x1, height: rect.y2 - rect.y1 };
}

// ---------------------------------------------------------------------------
// Mapping a library part onto a board's stack
// ---------------------------------------------------------------------------

/**
 * Move a component's geometry onto a different layer stack, matching by role.
 *
 * A footprint is authored against the standard stack — silk on Top Silk,
 * SMT copper on Top Copper, through-hole pads on the pad master. Once layers
 * are user-configurable those numbers no longer mean the same thing, so the
 * part is remapped by what each layer *is* rather than by its index.
 *
 * The pad master is preserved as the pad master: a through-hole pad must stay
 * on layer 0 to reach every copper layer, whatever else has been renumbered.
 */
export function remapComponentLayers(
  comp: ComponentEntity,
  source: readonly Layer[],
  target: readonly Layer[],
): number {
  let moved = 0;

  const remap = (entity: Entity): void => {
    if (entity.kind === 'component') {
      for (const child of entity.children) remap(child);
      return;
    }

    if (entity.layer === PAD_MASTER_LAYER) return;

    const from = source[entity.layer];
    if (!from) return;

    let to = target.findIndex(
      (l, i) => i !== PAD_MASTER_LAYER && l.type === from.type && l.bottom === from.bottom);
    if (to < 0) {
      to = target.findIndex((l, i) => i !== PAD_MASTER_LAYER && l.type === from.type);
    }
    if (to >= 0 && to !== entity.layer) {
      entity.layer = to;
      moved++;
    }
  };

  for (const child of comp.children) remap(child);
  return moved;
}

// ---------------------------------------------------------------------------
// Choosing a layer for a tool
// ---------------------------------------------------------------------------

/**
 * The layer a newly drawn primitive should land on.
 *
 * Drawing defaults to whatever layer happens to be selected otherwise, which
 * puts traces on silkscreen and text on copper. Each tool declares the layer
 * *types* it belongs on, and this picks the best visible, unlocked match —
 * preferring the current layer when it already qualifies, so an explicit
 * choice is never overridden.
 */
export function preferredLayer(
  doc: CadDocument,
  types: readonly LayerType[],
  current: number,
  opts: { includePadMaster?: boolean } = {},
): number {
  const allowMaster = opts.includePadMaster ?? false;

  const usable = (l: Layer | undefined, index: number): boolean => {
    if (l === undefined || !l.visible || l.locked) return false;
    // The pad master declares type Silk in the stock stack, so a search for a
    // silkscreen layer would otherwise match it and win on index alone —
    // putting board text on the pad master. Nothing draws there by default.
    if (!allowMaster && index === PAD_MASTER_LAYER) return false;
    return types.includes(l.type);
  };

  if (usable(doc.layers[current], current)) return current;

  // Prefer a top-side layer of the first requested type.
  for (const t of types) {
    const top = doc.layers.findIndex(
      (l, i) => usable(l, i) && l.type === t && !l.bottom);
    if (top >= 0) return top;
    const any = doc.layers.findIndex((l, i) => usable(l, i) && l.type === t);
    if (any >= 0) return any;
  }
  return current;
}
